import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Text } from "./Text";
import { useFontsLoadedSignal } from "../../../../hooks/useTextLineHeightPx";
import { TextAreaRef } from "antd/es/input/TextArea";

import { getFontSizeInPixels, getRelativeFontSize } from "../../../../helpers";
import {
  fitFontToBoxPx,
  maxWordWidthPx,
  measureTextInkHeightPx,
} from "../../../../helpers/textBoxFit";
import {
  CustomText,
  ITEM_TYPES,
  ParameteredText,
  Position,
  Size,
  TabKeys,
  TitleItem,
} from "../../../../types";
import { useTemplatesStatusStore } from "../../../../store/useTemplatesStatusStore";
import { mapRelativeToPixels } from "../../../../helpers/itemsHelper";
import {
  BORDER_COLOR,
  DEFAULT_RELATIVE_PADDING,
  TEXT_PREVIEW_SELECTOR,
} from "../../../../constants";
import { DraggableComponent } from "./DraggableComponent";
import { AnimationPreviewWrapper } from "./AnimationPreviewWrapper";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { sanitizeText } from "utils/text";

// Minimum height delta (px) that justifies auto-fitting a fixed-height text
// box; below this we treat the difference as rounding noise and leave the
// persisted height untouched.
const HEIGHT_FIT_EPSILON_PX = 1;

export const TextEditable = ({
  textItem,
  previewWidth,
  previewHeight,
  onEditStart,
  onEditFinish,
  onChange,
}: {
  textItem: CustomText | ParameteredText | (TitleItem & { content: string });
  previewWidth: number;
  previewHeight: number;
  onEditStart?: () => void;
  onEditFinish?: () => void;
  onChange: (params: Partial<CustomText | ParameteredText | TitleItem>) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const hasBeenClickedRef = useRef(false);
  const currentValueRef = useRef(textItem.content);
  const editableRef = useRef<TextAreaRef>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);

  const isTitleItem = "showTitle" in textItem;

  const { currentItem } = useTemplateEditorStore((store) => ({
    currentItem: store.templateState.currentItem,
  }));

  const { setActiveTab, activeTab } = useTemplatesStatusStore((store) => ({
    setActiveTab: store.setActiveTab,
    activeTab: store.activeTab,
  }));

  const isCurrent = isTitleItem
    ? currentItem?.type === textItem.id
    : currentItem?.id === textItem.id;

  useEffect(() => {
    if (isTitleItem) {
      if (currentItem?.type !== ITEM_TYPES.Title) {
        hasBeenClickedRef.current = false;
      }
    } else {
      if (currentItem?.id !== textItem.id) {
        hasBeenClickedRef.current = false;
      }
    }
  }, [currentItem, textItem.id, isTitleItem, setIsEditing]);

  const itemHasNullHeight = textItem.size.height == null;
  const hasBackground = !!textItem.backgroundColor;

  // Latest-callback ref so the auto-fit effect below doesn't depend on
  // `onChange` identity (parents pass fresh closures every render). Without
  // this the effect re-runs — and re-measures — on every render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Baseline of the last typography we reconciled the container height against,
  // scoped to the currently rendered item. `null` until the first measurement
  // settles. Lets the auto-fit fire ONLY on a real font / font-size / line-height
  // change — never on mount, slide switch, or web-font load.
  const fontKey = `${textItem.fontPath ?? ""}|${textItem.fontAssetId ?? ""}`;
  const fittedRef = useRef<{
    id?: string;
    fontSize: number;
    lineHeight: number;
    fontKey: string;
  } | null>(null);

  const verticalAlign =
    "verticalAlign" in textItem ? textItem.verticalAlign : undefined;

  // Web-font load signal — when the FONT changes, the new face loads async, so
  // re-run the fit once it's ready (measuring before then uses fallback metrics).
  const fontsTick = useFontsLoadedSignal();

  // Grow the container height to fit the text when the font, font size, or line
  // height changes (fixed-height items only). Per product: the box grows to the
  // font (it never shrinks the font), GROW-ONLY, anchored by vertical alignment
  // (top → down, bottom → up, center → both). Must not run on initial mount or
  // scene switch, which would measure freshly-rendered (possibly unloaded) text
  // and trigger a phantom autosave — so we baseline per item and only fit on a
  // subsequent, genuine typography change.
  useLayoutEffect(() => {
    if (itemHasNullHeight || isEditing) return;

    // Mount or slide switch (new item): record the baseline, don't fit.
    if (!fittedRef.current || fittedRef.current.id !== textItem.id) {
      fittedRef.current = {
        id: textItem.id,
        fontSize: textItem.fontSize,
        lineHeight: textItem.lineHeight,
        fontKey,
      };
      return;
    }

    // Re-run not caused by a typography change (e.g. preview resize or a
    // size write-back): nothing to fit.
    const changed =
      fittedRef.current.fontSize !== textItem.fontSize ||
      fittedRef.current.lineHeight !== textItem.lineHeight ||
      fittedRef.current.fontKey !== fontKey;
    if (!changed) return;

    const container = textContainerRef.current;
    if (!container) return;

    const textElement = container.querySelector(TEXT_PREVIEW_SELECTOR);
    if (!textElement || !(textElement instanceof HTMLElement)) return;

    // If the (possibly new) font isn't loaded yet, defer: leave the baseline
    // untouched so this effect re-runs and fits once `fontsTick` advances.
    const computed = getComputedStyle(textElement);
    const fontShorthand = `${computed.fontSize} ${computed.fontFamily}`;
    if (
      typeof document.fonts.check === "function" &&
      !document.fonts.check(fontShorthand)
    ) {
      return;
    }

    fittedRef.current = {
      id: textItem.id,
      fontSize: textItem.fontSize,
      lineHeight: textItem.lineHeight,
      fontKey,
    };

    const fontSizePx = parseFloat(computed.fontSize) || 0;
    const lineHeightPx = parseFloat(computed.lineHeight) || 0;
    const margin = hasBackground ? DEFAULT_RELATIVE_PADDING * fontSizePx : 0;

    const wrapText = textItem.wrapText ?? true;
    const currentWidthPx = mapRelativeToPixels(
      textItem.size.width,
      previewWidth
    );
    const currentHeightPx = mapRelativeToPixels(
      textItem.size.height ?? 0,
      previewHeight
    );

    // Width: grow if the widest word (as rendered) no longer fits.
    let newWidthPx = currentWidthPx;
    if (wrapText) {
      const neededWidthPx =
        maxWordWidthPx(textElement, textItem.content) + margin * 2;
      newWidthPx = Math.max(currentWidthPx, neededWidthPx);
    }

    // Height: grow to the glyph INK height of the block (deterministic, measured
    // with the rendered font + text-transform), at the possibly-grown width, plus
    // the background margin. Flush both edges → symmetric padding.
    const inkHeight = measureTextInkHeightPx(
      textElement,
      textItem.content,
      newWidthPx - margin * 2,
      lineHeightPx,
      wrapText
    );
    if (inkHeight == null) return; // font not ready — retry on fontsTick
    const newHeightPx = inkHeight + margin * 2;

    const growsWidth = newWidthPx - currentWidthPx > HEIGHT_FIT_EPSILON_PX;
    const growsHeight = newHeightPx - currentHeightPx > HEIGHT_FIT_EPSILON_PX;
    if (!growsWidth && !growsHeight) return;

    // Anchor the growth: vertical by vertical-align, horizontal by text-align.
    const deltaWRel = growsWidth
      ? (newWidthPx - currentWidthPx) / previewWidth
      : 0;
    const deltaHRel = growsHeight
      ? (newHeightPx - currentHeightPx) / previewHeight
      : 0;
    const newX =
      textItem.textAlign === "right"
        ? textItem.position.x - deltaWRel
        : textItem.textAlign === "center"
          ? textItem.position.x - deltaWRel / 2
          : textItem.position.x; // left (default): grow rightward
    const newY =
      verticalAlign === "bottom"
        ? textItem.position.y - deltaHRel
        : verticalAlign === "center"
          ? textItem.position.y - deltaHRel / 2
          : textItem.position.y; // top (default): grow downward

    onChangeRef.current({
      id: textItem.id,
      size: {
        width: growsWidth ? newWidthPx / previewWidth : textItem.size.width,
        height: growsHeight
          ? newHeightPx / previewHeight
          : textItem.size.height,
      },
      position: { x: newX, y: newY },
    });
  }, [
    textItem.fontSize,
    textItem.lineHeight,
    fontKey,
    verticalAlign,
    fontsTick,
    itemHasNullHeight,
    isEditing,
    previewWidth,
    previewHeight,
    hasBackground,
    textItem.id,
    textItem.content,
    textItem.textAlign,
    textItem.wrapText,
    textItem.position,
    textItem.size,
  ]);

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    currentValueRef.current = e.target.value;
  };

  const handleEnterKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") {
      handleFinishEditing();
    }
  };

  const handleEditStart = () => {
    setIsEditing(true);
    onEditStart?.();
    if (!editableRef.current) return;
    editableRef.current.focus();
    // Set cursor to end of text
    const textArea = editableRef.current.resizableTextArea?.textArea;
    if (!textArea) return;
    textArea.setSelectionRange(textArea.value.length, textArea.value.length);
  };

  const handleFinishEditing = () => {
    setIsEditing(false);
    const textArea = editableRef.current?.resizableTextArea?.textArea;
    const boundingRect = textArea?.getBoundingClientRect();

    // Measure the actual content height needed
    let contentHeight = boundingRect?.height;
    if (textArea && !itemHasNullHeight) {
      const originalHeight = textArea.style.height;
      textArea.style.height = "auto";
      contentHeight = Math.max(
        textArea.scrollHeight,
        boundingRect?.height ?? 0
      );
      textArea.style.height = originalHeight;
    }

    const content = sanitizeText(currentValueRef.current);
    const newSize = boundingRect
      ? {
          width: boundingRect.width / previewWidth,
          height: itemHasNullHeight
            ? null
            : (contentHeight ?? 0) / previewHeight,
        }
      : textItem.size;

    // Don't persist a shrunk font size — the stored size is the user's max and
    // the auto-fit is applied for display at render time (useFittedFontSize).
    onChange({
      id: textItem.id,
      content,
      size: newSize,
    });
    onEditFinish?.();
  };

  const handleFinishEditingRef = useRef(handleFinishEditing);
  useEffect(() => {
    handleFinishEditingRef.current = handleFinishEditing;
  });

  // Save content when another item becomes current while editing
  useEffect(() => {
    if (!isCurrent && isEditing) {
      handleFinishEditingRef.current();
    }
  }, [isCurrent, isEditing]);

  const handleClick = () => {
    if (!isCurrent) return;
    if (hasBeenClickedRef.current) {
      handleEditStart();
    } else {
      hasBeenClickedRef.current = true;
    }
  };

  const handleFocus = () => {
    if (activeTab !== TabKeys.content && activeTab !== TabKeys.motion) {
      setActiveTab(TabKeys.content);
    }
  };

  const handleDragStop = (params: { position?: Position; size?: Size }) => {
    const newSize = params.size ?? textItem.size;

    // Shrinking the box: if the text no longer fits the new box (either axis),
    // adjust the font down to fit. (Growing the box leaves the font as-is.)
    let fontSizeUpdate: number | undefined;
    if (!itemHasNullHeight && newSize.height != null && textItem.content) {
      const textElement = textContainerRef.current?.querySelector(
        TEXT_PREVIEW_SELECTOR
      );
      if (textElement instanceof HTMLElement) {
        const currentFontPx = getFontSizeInPixels(
          textItem.fontSize,
          previewWidth
        );
        const fittedPx = fitFontToBoxPx({
          el: textElement,
          text: textItem.content,
          boxWidthPx: newSize.width * previewWidth,
          boxHeightPx: newSize.height * previewHeight,
          lineHeightRatio: textItem.lineHeight,
          wrapText: textItem.wrapText ?? true,
          hasBackground,
          maxFontPx: currentFontPx,
        });
        if (currentFontPx - fittedPx > 0.5) {
          fontSizeUpdate = getRelativeFontSize(fittedPx, previewWidth);
        }
      }
    }

    onChange({
      id: textItem.id,
      position: params.position ?? textItem.position,
      size: newSize,
      ...(fontSizeUpdate !== undefined && { fontSize: fontSizeUpdate }),
    });
    hasBeenClickedRef.current = false;
  };

  const textComponentProps = {
    textFragment: textItem.content,
    textItem,
    previewWidth,
    previewHeight,
  };

  return isEditing && isCurrent ? (
    <div
      key={textItem.id}
      style={{
        position: "absolute",
        top: mapRelativeToPixels(textItem.position.y, previewHeight),
        left: mapRelativeToPixels(textItem.position.x, previewWidth),
        width: mapRelativeToPixels(textItem.size.width, previewWidth),
      }}
    >
      <Text
        editable
        editableRef={editableRef}
        editableProps={{
          onBlur: handleFinishEditing,
          onChange: handleInput,
          onPressEnter: handleEnterKey,
        }}
        {...textComponentProps}
        styles={{
          outline: `2px solid ${BORDER_COLOR}`,
          borderRadius: 0,
        }}
      />
    </div>
  ) : (
    <DraggableComponent
      key={textItem.id}
      position={textItem.position}
      size={textItem.size as Size}
      rotation={textItem.rotation}
      layerIndex={textItem.layerIndex}
      onDragStop={handleDragStop}
      onRotate={(rotation) => onChange({ id: textItem.id, rotation })}
      drag={true}
      resize={true}
      item={{
        id: textItem.id,
        type: isTitleItem ? ITEM_TYPES.Title : ITEM_TYPES.CustomText,
      }}
      onFocus={handleFocus}
      previewWidth={previewWidth}
      previewHeight={previewHeight}
      onEditStart={handleEditStart}
    >
      <AnimationPreviewWrapper
        animations={textItem.animations}
        previewWidth={previewWidth}
        previewHeight={previewHeight}
        itemId={isTitleItem ? ITEM_TYPES.Title : textItem.id}
        rotation={textItem.rotation}
      >
        <div
          ref={textContainerRef}
          role="button"
          tabIndex={0}
          onClick={handleClick}
          onDoubleClick={handleEditStart}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleClick();
            }
          }}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
          }}
        >
          <Text {...textComponentProps} fillContainer />
        </div>
      </AnimationPreviewWrapper>
    </DraggableComponent>
  );
};
